import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  ConstructorPage,
  isComponentTemplate,
  useConstructor,
  type ConstructorValue,
} from '@game-platform/theme-constructor';
import { operatorConsole, type PoolOfferConfig } from '../../lib/api/operatorConsole';
import { isCustomPackPicked, LAB_DEMO_PACK } from './labDemoPack';
import { PackSelector } from './PackSelector';
import { TemplateSelector } from './TemplateSelector';
import { useDefaultTemplate, type TemplateName } from './useDefaultTemplate';

const IFRAME_ORIGIN = import.meta.env.VITE_IFRAME_ORIGIN || 'http://localhost:7474';

interface SaveMessage {
  type: 'success' | 'error';
  text: string;
}

/**
 * SaveSlot — primary action button. Lives inside ConstructorPage's provider
 * scope so it can read current state via useConstructor and POST it on click.
 * Matches the topbar's h-7 control height, painted green to stand out as
 * the primary action.
 */
function SaveSlot({
  saving,
  message,
  onSave,
}: {
  saving: boolean;
  message: SaveMessage | null;
  onSave: (value: ConstructorValue) => Promise<void>;
}) {
  const { state } = useConstructor();

  return (
    <div className="flex items-center gap-3">
      {message && (
        <span
          className={`font-mono text-[11px] ${
            message.type === 'success' ? 'text-emerald-400' : 'text-red-400'
          }`}
        >
          {message.text}
        </span>
      )}
      <button
        type="button"
        onClick={() => void onSave(state.value)}
        disabled={saving}
        className="h-7 inline-flex items-center justify-center rounded border border-emerald-600 bg-emerald-600 px-3 font-mono text-[11px] font-semibold text-white outline-none transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

export function ThemeEditorPage() {
  const { template } = useParams<{ template: string }>();
  const navigate = useNavigate();
  const defaultTemplate = useDefaultTemplate();

  // URL is the single source of truth for which template is being edited.
  // Deriving `activeTemplate` (instead of mirroring it into a useState slot)
  // removes a race where two effects fought to sync route ↔ state and the
  // browser ended up in a redirect loop on /themes/modern — the URL would
  // flip back to /themes/classic on every render until the tab froze.
  const activeTemplate: TemplateName = isComponentTemplate(template) ? template : 'classic';
  const setActiveTemplate = (next: TemplateName) => {
    if (next !== activeTemplate) navigate(`/themes/${next}`, { replace: true });
  };

  const [initialValue, setInitialValue] = useState<ConstructorValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<SaveMessage | null>(null);

  // Iframe session auto-generation
  const [token, setToken] = useState('');
  const [gameId, setGameId] = useState('');
  const [packs, setPacks] = useState<PoolOfferConfig[]>([]);
  const [packsError, setPacksError] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Player ID for preview session — required, validated against the operator.
  const [playerIdInput, setPlayerIdInput] = useState('');
  const [appliedPlayerId, setAppliedPlayerId] = useState('');

  // Load theme for active template.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const t = await operatorConsole.getTemplateTheme(activeTemplate);
        if (!cancelled) {
          setInitialValue(t.design_system as unknown as ConstructorValue);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load theme');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTemplate]);

  // Fetch session token when template or applied player ID changes.
  // player_id is required by the backend — skip the (guaranteed-to-fail)
  // request until one has been entered and applied via Load.
  useEffect(() => {
    if (!appliedPlayerId) {
      setToken('');
      setTokenError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await operatorConsole.iframeUrl({
          template: activeTemplate,
          player_id: appliedPlayerId,
        });
        if (!cancelled) {
          setToken(resp.session_token);
          setTokenError(null);
        }
      } catch (e) {
        // Surface the error so we can see WHY /iframe_url failed instead of
        // silently leaving an empty token (which makes the preview iframe
        // never load).
        console.error('Failed to fetch iframe_url:', e);
        if (!cancelled) {
          setToken('');
          setTokenError(e instanceof Error ? e.message : 'Failed to generate preview token');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTemplate, appliedPlayerId]);

  // Load available pool offer configs on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // available_only=true → live packs only. Deliberately WITHOUT
        // include_staged: the preview launches a real session, and a staged
        // pack is absent from the player catalog it renders, so offering one
        // here would just be a dead entry in the selector.
        const resp = await operatorConsole.listPoolOfferConfigs({ availableOnly: true });
        if (!cancelled) {
          setPacks(resp.pool_offer_configs);
          const firstPack = resp.pool_offer_configs[0];
          if (firstPack) {
            setGameId(String(firstPack.pool_offer_id));
          }
          setPacksError(null);
        }
      } catch {
        if (!cancelled) {
          setPacksError('Failed to load packs');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (value: ConstructorValue) => {
    try {
      setSaving(true);
      setMessage(null);
      await operatorConsole.putTemplateTheme(activeTemplate, value as unknown as Record<string, unknown>);
      setMessage({ type: 'success', text: 'Theme saved' });
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to save',
      });
    } finally {
      setSaving(false);
    }
  };

  // The demo entry names no game: the preview opens the custom pack lab on fixtures.
  const labDemo = gameId === LAB_DEMO_PACK;
  // The editor lists the lab, and its device presets, only while a custom pack or the demo is what is previewed.
  const labActive = labDemo || isCustomPackPicked(packs, gameId);

  if (loading || !initialValue) {
    return (
      <div className="p-6 text-[14px] text-[var(--ui-text-subtext,#878B95)]">
        Loading theme...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex flex-col gap-3">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  }

  return (
    <ConstructorPage
      key={activeTemplate}
      iframeOrigin={IFRAME_ORIGIN}
      initialValue={initialValue}
      controlledTemplate={activeTemplate}
      onTemplateChange={setActiveTemplate}
      hideSessionInputs={true}
      hideTemplateRadio={true}
      token={token}
      gameId={labDemo ? '' : gameId}
      labDemo={labDemo}
      labActive={labActive}
      onTokenChange={setToken}
      onGameIdChange={setGameId}
      sidebarVariant="dark"
      leadingControls={
        <div className="flex items-center gap-2">
          <TemplateSelector
            template={activeTemplate}
            onTemplateChange={setActiveTemplate}
            defaultTemplate={defaultTemplate}
          />
          <PackSelector
            packs={packs}
            gameId={gameId}
            onGameIdChange={setGameId}
          />
          <label
            className="flex items-center gap-1"
            title="The player token your platform passes at launch (sent to your get_session callback), not the account id"
          >
            <span className="font-mono text-[10px] uppercase tracking-wide text-neutral-400 whitespace-nowrap">
              Player token
            </span>
            <input
              type="text"
              value={playerIdInput}
              onChange={(e) => setPlayerIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setAppliedPlayerId(playerIdInput.trim());
              }}
              placeholder="required"
              className="h-7 w-32 rounded border border-neutral-700 bg-neutral-900 px-2 font-mono text-[11px] text-white outline-none placeholder:text-neutral-600"
            />
            <button
              type="button"
              onClick={() => setAppliedPlayerId(playerIdInput.trim())}
              disabled={playerIdInput.trim() === appliedPlayerId}
              className="h-7 rounded border border-neutral-700 bg-neutral-900 px-2 font-mono text-[11px] text-white outline-none hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Load
            </button>
            {appliedPlayerId && (
              <button
                type="button"
                onClick={() => { setPlayerIdInput(''); setAppliedPlayerId(''); }}
                className="h-7 rounded border border-neutral-700 bg-neutral-900 px-2 font-mono text-[11px] text-neutral-400 outline-none hover:bg-neutral-800"
                title="Clear player token"
              >
                ✕
              </button>
            )}
          </label>
          {tokenError && (
            <span
              className="max-w-[260px] truncate font-mono text-[11px] text-red-400"
              title={tokenError}
            >
              token: {tokenError}
            </span>
          )}
          {packsError && (
            <span className="font-mono text-[11px] text-red-400">{packsError}</span>
          )}
        </div>
      }
      trailingAction={
        <SaveSlot saving={saving} message={message} onSave={handleSave} />
      }
    />
  );
}
